import {
  Breadcrumbs,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Add,
  Archive,
  ArrowBack,
  ArrowDownward,
  ArrowUpward,
  DeleteForever,
  DragIndicator,
  Edit,
  Group,
  Refresh,
} from "@material-ui/icons";
import Alert from "@material-ui/lab/Alert";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { taskRequest } from "./api";
import BoardMembersDialog from "./BoardMembersDialog";
import BoardPicker from "./BoardPicker";
import BoardScrollbar from "./BoardScrollbar";
import TaskDialog, { userName } from "./TaskDialog";
import { formatDeadline, sortTasks } from "./taskSorting";
import { getUser } from "../../Utils/Common";
import { listSortStorageKey, readListSortPreferences, saveListSortPreferences } from "./listSortPreferences";

const useStyles = makeStyles((theme) => ({
  root: { width: "100%", minWidth: 0 },
  toolbar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    margin: "16px 0",
  },
  board: {
    display: "flex",
    gap: 16,
    overflowX: "auto",
    alignItems: "flex-start",
    paddingBottom: 24,
    minHeight: 280,
  },
  list: {
    flex: "0 0 300px",
    maxWidth: "85vw",
    padding: 12,
    backgroundColor: theme.palette.background.default,
    border: "2px solid transparent",
  },
  over: { borderColor: theme.palette.primary.main },
  listHeader: { display: "flex", alignItems: "center", marginBottom: 8 },
  listHandle: { display: "flex", alignItems: "center", flex: 1, minWidth: 0, cursor: "grab" },
  listDropBefore: { boxShadow: `inset 3px 0 ${theme.palette.primary.main}` },
  listDropAfter: { boxShadow: `inset -3px 0 ${theme.palette.primary.main}` },
  card: { marginBottom: 10, cursor: "grab" },
  dropBefore: { boxShadow: `inset 0 3px ${theme.palette.primary.main}` },
  dropAfter: { boxShadow: `inset 0 -3px ${theme.palette.primary.main}` },
  description: {
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    marginTop: 8,
  },
  chips: { display: "flex", flexWrap: "wrap", gap: 4, marginTop: 12 },
  empty: { padding: "24px 8px", textAlign: "center" },
}));

export default function Tasks() {
  const [selectedBoard, setSelectedBoard] = useState(null);
  const userId = getUser()?.userId;
  return selectedBoard ? (
    <TaskBoardView
      key={`${userId}:${selectedBoard.id}`}
      userId={userId}
      selectedBoard={selectedBoard}
      onBack={() => setSelectedBoard(null)}
    />
  ) : (
    <BoardPicker onSelect={setSelectedBoard} />
  );
}

function TaskBoardView({ selectedBoard, userId, onBack }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const [board, setBoard] = useState({ lists: [], tasks: [], users: [] });
  const [archived, setArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [selection, setSelection] = useState(null);
  const [showMembers, setShowMembers] = useState(false);
  const [listEditor, setListEditor] = useState(null);
  const [listError, setListError] = useState("");
  const [dragging, setDragging] = useState(null);
  const [draggingList, setDraggingList] = useState(null);
  const [listDropTarget, setListDropTarget] = useState(null);
  const [over, setOver] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const sortStorageKey = listSortStorageKey(userId, selectedBoard.id);
  const [sortPreferences, setSortPreferences] = useState(() => readListSortPreferences(sortStorageKey));
  const { sortModes, reversedLists } = sortPreferences;
  useEffect(() => {
    saveListSortPreferences(sortStorageKey, sortPreferences);
  }, [sortStorageKey, sortPreferences]);
  const requestId = useRef(0);
  const boardRef = useRef(null);
  const mutation = useRef(false);
  const load = useCallback(async () => {
    const current = ++requestId.current;
    setLoading(true);
    setError("");
    try {
      const result = await taskRequest("get_task_board", {
        boardId: selectedBoard.id,
        archived,
      });
      if (current === requestId.current) setBoard(result);
    } catch (err) {
      if (current === requestId.current) setError(err.message);
    } finally {
      if (current === requestId.current) setLoading(false);
    }
  }, [archived, selectedBoard.id]);
  useEffect(() => {
    load();
    return () => {
      requestId.current += 1;
    };
  }, [load]);

  const saveList = async () => {
    if (mutation.current) return;
    mutation.current = true;
    setBusy(true);
    setListError("");
    try {
      await taskRequest("save_task_list", {
        boardId: selectedBoard.id,
        listId: listEditor.id,
        name: listEditor.name,
      });
      setListEditor(null);
      await load();
    } catch (err) {
      setListError(err.message);
    } finally {
      setBusy(false);
      mutation.current = false;
    }
  };
  const deleteList = async () => {
    if (mutation.current) return;
    mutation.current = true;
    setBusy(true);
    setListError("");
    try {
      await taskRequest("delete_task_list", {
        boardId: selectedBoard.id,
        listId: listEditor.id,
      });
      setListEditor(null);
      await load();
    } catch (err) {
      setListError(err.message);
    } finally {
      setBusy(false);
      mutation.current = false;
    }
  };
  const repositionTask = async (taskId, listId, beforeTaskId = null) => {
    if (mutation.current || archived || loading) return;
    const task = board.tasks.find((item) => item.id === taskId);
    if (
      !task ||
      taskId === beforeTaskId ||
      (sortModes[listId] || "manual") !== "manual"
    )
      return;
    // The API uses saved ascending order; translate the visible insertion point when reversed.
    if (reversedLists[listId]) {
      const visible = sortTasks(
        board.tasks.filter(
          (item) =>
            item.TaskListId === listId && !item.archived && item.id !== taskId,
        ),
        "manual",
        true,
      );
      const index =
        beforeTaskId === null
          ? visible.length
          : visible.findIndex((item) => item.id === beforeTaskId);
      if (index < 0) return;
      beforeTaskId = index > 0 ? visible[index - 1].id : null;
    }
    mutation.current = true;
    setBusy(true);
    setError("");
    try {
      const result = await taskRequest("move_task", {
        taskId,
        listId,
        beforeTaskId,
        revision: task.revision,
      });
      setBoard((previous) => ({
        ...previous,
        tasks: previous.tasks
          .map(
            (item) =>
              result.tasks.find((updated) => updated.id === item.id) || item,
          )
          .concat(
            result.tasks.filter(
              (updated) =>
                !previous.tasks.some((item) => item.id === updated.id),
            ),
          ),
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      mutation.current = false;
    }
  };
  const moveTask = (event, listId, beforeTaskId = null) => {
    if (draggingList) return;
    event.preventDefault();
    event.stopPropagation();
    const taskId = Number(event.dataTransfer.getData("application/x-task"));
    setOver(null);
    setDragging(null);
    setDropTarget(null);
    repositionTask(taskId, listId, beforeTaskId);
  };
  const repositionList = async (listId, beforeListId = null) => {
    if (mutation.current || archived || loading || listId === beforeListId) return;
    const list = board.lists.find((item) => item.id === listId);
    if (!list) return;
    const ordered = board.lists.filter((item) => item.id !== listId);
    const index = beforeListId === null ? ordered.length : ordered.findIndex((item) => item.id === beforeListId);
    if (index < 0) return;
    ordered.splice(index, 0, list);
    if (ordered.every((item, i) => item.id === board.lists[i].id)) return;
    mutation.current = true;
    setBusy(true);
    setError("");
    try {
      const result = await taskRequest("move_task_list", {
        boardId: selectedBoard.id, listId, beforeListId,
        listOrder: board.lists.map((item) => item.id),
      });
      setBoard((previous) => ({ ...previous, lists: result.lists }));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      mutation.current = false;
    }
  };
  const endListDrag = () => {
    setDraggingList(null);
    setListDropTarget(null);
  };
  const startListDrag = (event, listId) => {
    if (mutation.current || archived || loading) {
      event.preventDefault();
      return;
    }
    event.stopPropagation();
    event.dataTransfer.setData("application/x-task-list", String(listId));
    event.dataTransfer.effectAllowed = "move";
    setDraggingList(listId);
    setDragging(null);
    setOver(null);
    setDropTarget(null);
  };
  const dropList = (event, targetId) => {
    event.preventDefault();
    event.stopPropagation();
    const listId = Number(event.dataTransfer.getData("application/x-task-list"));
    const rect = event.currentTarget.getBoundingClientRect();
    const after = rect.width > 0 && event.clientX > rect.left + rect.width / 2;
    endListDrag();
    if (listId !== draggingList || listId === targetId) return;
    const targetIndex = board.lists.findIndex((item) => item.id === targetId);
    const next = board.lists.slice(targetIndex + 1).find((item) => item.id !== listId);
    repositionList(listId, after ? (next ? next.id : null) : targetId);
  };

  return (
    <div className={classes.root}>
      <Breadcrumbs>
        <Link
          color="inherit"
          disabled={busy}
          onClick={onBack}
          style={{ cursor: "pointer" }}
        >
          {t("tasks.boards")}
        </Link>
        <Typography>{selectedBoard.name}</Typography>
      </Breadcrumbs>
      <div className={classes.toolbar}>
        <Typography component="h1" variant="h5" style={{ flexGrow: 1 }}>
          {selectedBoard.name}
          {archived ? ` — ${t("tasks.archiveHeader")}` : ""}
        </Typography>
        <Button startIcon={<ArrowBack />} disabled={busy} onClick={onBack}>
          {t("tasks.backToBoards")}
        </Button>
        <Button
          startIcon={<Group />}
          disabled={busy || loading}
          onClick={() => setShowMembers(true)}
        >
          {t("tasks.members")}
        </Button>
        <Button
          startIcon={<Archive />}
          disabled={busy || loading}
          onClick={() => setArchived(!archived)}
        >
          {t(archived ? "tasks.backToBoard" : "tasks.showArchive")}
        </Button>
        <IconButton
          aria-label={t("tasks.refresh")}
          disabled={busy || loading}
          onClick={load}
        >
          <Refresh />
        </IconButton>
        {!archived && (
          <Button
            color="primary"
            variant="contained"
            startIcon={<Add />}
            disabled={busy || loading}
            onClick={() => {
              setListError("");
              setListEditor({ name: "" });
            }}
          >
            {t("tasks.addList")}
          </Button>
        )}
      </div>
      {error && (
        <Alert severity="error" role="alert" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}
      {busy && (
        <Typography role="status" color="textSecondary">
          {t("tasks.saving")}
        </Typography>
      )}
      {loading ? (
        <CircularProgress aria-label={t("tasks.loading")} />
      ) : (
        <>
          {!board.lists.length && !error && (
            <Paper className={classes.empty}>
              <Typography>{t("tasks.emptyBoard")}</Typography>
            </Paper>
          )}
          {archived && !board.tasks.length && (
            <Typography className={classes.empty}>
              {t("tasks.emptyArchive")}
            </Typography>
          )}
          <div className={classes.board} ref={boardRef}>
            {board.lists.map((list, listIndex) => {
              const sortMode = sortModes[list.id] || "manual";
              const reversed = !!reversedLists[list.id];
              const cards = sortTasks(
                board.tasks.filter(
                  (task) =>
                    task.TaskListId === list.id && !!task.archived === archived,
                ),
                sortMode,
                reversed,
              );
              if (archived && !cards.length) return null;
              return (
                <Paper
                  component="section"
                  aria-label={list.name}
                  data-list-id={list.id}
                  key={list.id}
                  className={`${classes.list} ${over === list.id ? classes.over : ""} ${listDropTarget && listDropTarget.id === list.id ? (listDropTarget.after ? classes.listDropAfter : classes.listDropBefore) : ""}`}
                  style={{ opacity: draggingList === list.id ? 0.5 : 1 }}
                  onDragOver={(event) => {
                    if (draggingList && !archived && !busy) {
                      event.preventDefault();
                      event.stopPropagation();
                      event.dataTransfer.dropEffect = "move";
                      const rect = event.currentTarget.getBoundingClientRect();
                      setListDropTarget({ id: list.id, after: rect.width > 0 && event.clientX > rect.left + rect.width / 2 });
                      return;
                    }
                    if (
                      dragging &&
                      !archived &&
                      !busy &&
                      sortMode === "manual"
                    ) {
                      event.preventDefault();
                      event.dataTransfer.dropEffect = "move";
                      setOver(list.id);
                      setDropTarget(null);
                    }
                  }}
                  onDragLeave={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) {
                      setOver(null);
                      setListDropTarget(null);
                    }
                  }}
                  onDrop={(event) => draggingList ? dropList(event, list.id) : moveTask(event, list.id)}
                >
                  <div className={classes.listHeader}>
                    <div className={classes.listHandle}
                      draggable={!busy && !archived}
                      onDragStart={(event) => startListDrag(event, list.id)}
                      onDragEnd={endListDrag}>
                      {!archived && <IconButton size="small" disabled={busy}
                        aria-label={`${t("tasks.moveList")}: ${list.name}`} title={t("tasks.moveListHint")}
                        onKeyDown={(event) => {
                          if (event.key === "ArrowLeft" && listIndex > 0) {
                            event.preventDefault();
                            repositionList(list.id, board.lists[listIndex - 1].id);
                          } else if (event.key === "ArrowRight" && listIndex < board.lists.length - 1) {
                            event.preventDefault();
                            repositionList(list.id, board.lists[listIndex + 2] ? board.lists[listIndex + 2].id : null);
                          }
                        }}><DragIndicator fontSize="small" /></IconButton>}
                      <Typography
                        component="h2"
                        variant="subtitle1"
                        style={{ flexGrow: 1, overflowWrap: "anywhere" }}
                      >
                        {list.name} ({cards.length})
                      </Typography>
                    </div>
                    {!archived && (
                      <IconButton
                        size="small"
                        aria-label={t("tasks.renameList")}
                        disabled={busy}
                        onClick={() => {
                          setListError("");
                          setListEditor({ id: list.id, name: list.name });
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 4,
                    }}
                  >
                    <TextField
                      select
                      fullWidth
                      variant="outlined"
                      margin="dense"
                      id={`task-sort-${list.id}`}
                      label={t("tasks.sortBy")}
                      value={sortMode}
                      disabled={busy}
                      SelectProps={{ native: true }}
                      helperText={
                        sortMode !== "manual" && !archived
                          ? t("tasks.manualOrderHint")
                          : ""
                      }
                      onChange={(event) => {
                        const mode = event.target.value;
                        setSortPreferences((previous) => ({
                          ...previous,
                          sortModes: { ...previous.sortModes, [list.id]: mode },
                        }));
                      }}
                    >
                      <option value="manual">
                        {t(
                          reversed
                            ? "tasks.sortManualReverse"
                            : "tasks.sortManual",
                        )}
                      </option>
                      <option value="deadline">
                        {t(
                          reversed
                            ? "tasks.sortDeadlineReverse"
                            : "tasks.sortDeadline",
                        )}
                      </option>
                      <option value="title">
                        {t(
                          reversed
                            ? "tasks.sortTitleReverse"
                            : "tasks.sortTitle",
                        )}
                      </option>
                    </TextField>
                    <IconButton
                      aria-label={`${t("tasks.reverseOrder")}: ${list.name}`}
                      title={t("tasks.reverseOrder")}
                      aria-pressed={reversed}
                      disabled={busy}
                      style={{ marginTop: 8 }}
                      onClick={() =>
                        setSortPreferences((previous) => ({
                          ...previous,
                          reversedLists: { ...previous.reversedLists, [list.id]: !previous.reversedLists[list.id] },
                        }))
                      }
                    >
                      {reversed ? <ArrowDownward /> : <ArrowUpward />}
                    </IconButton>
                  </div>
                  {cards.map((task, index) => (
                    <Card
                      key={task.id}
                      data-task-id={task.id}
                      className={`${classes.card} ${dropTarget && dropTarget.id === task.id ? (dropTarget.after ? classes.dropAfter : classes.dropBefore) : ""}`}
                      draggable={!busy && !archived && sortMode === "manual"}
                      style={{
                        opacity: dragging === task.id ? 0.5 : 1,
                        backgroundColor: task.backgroundColor || undefined,
                        color: task.color || undefined,
                      }}
                      onDragOver={(event) => {
                        if (
                          !dragging ||
                          busy ||
                          archived ||
                          sortMode !== "manual"
                        )
                          return;
                        event.preventDefault();
                        event.stopPropagation();
                        const rect =
                          event.currentTarget.getBoundingClientRect();
                        setOver(list.id);
                        setDropTarget({
                          id: task.id,
                          after:
                            rect.height > 0 &&
                            event.clientY > rect.top + rect.height / 2,
                        });
                      }}
                      onDrop={(event) => {
                        if (draggingList) return;
                        const rect =
                          event.currentTarget.getBoundingClientRect();
                        const after =
                          rect.height > 0 &&
                          event.clientY > rect.top + rect.height / 2;
                        const movedId = Number(
                          event.dataTransfer.getData("application/x-task"),
                        );
                        const next = cards
                          .slice(index + 1)
                          .find((item) => item.id !== movedId);
                        moveTask(
                          event,
                          list.id,
                          after ? (next ? next.id : null) : task.id,
                        );
                      }}
                      onDragStart={(event) => {
                        event.dataTransfer.setData(
                          "application/x-task",
                          String(task.id),
                        );
                        event.dataTransfer.effectAllowed = "move";
                        setDragging(task.id);
                      }}
                      onDragEnd={() => {
                        setDragging(null);
                        setOver(null);
                        setDropTarget(null);
                      }}
                    >
                      <CardActionArea
                        disabled={busy}
                        onClick={() => setSelection({ id: task.id })}
                      >
                        <CardContent>
                          <Typography
                            style={{
                              fontWeight: 600,
                              overflowWrap: "anywhere",
                            }}
                          >
                            {task.title}
                          </Typography>
                          {task.description && (
                            <Typography
                              color={task.color ? "inherit" : "textSecondary"}
                              variant="body2"
                              className={classes.description}
                            >
                              {task.description}
                            </Typography>
                          )}
                          {task.deadline && (
                            <Typography
                              variant="body2"
                              color={task.color ? "inherit" : "textSecondary"}
                              style={{ marginTop: 8 }}
                            >
                              {t("tasks.deadline")}:{" "}
                              {formatDeadline(task.deadline)}
                            </Typography>
                          )}
                          {task.eventStart && (
                            <Typography
                              variant="body2"
                              color={task.color ? "inherit" : "textSecondary"}
                              style={{ marginTop: 8 }}
                            >
                              {t("tasks.fields.eventStart")}:{" "}
                              {new Date(task.eventStart).toLocaleString()}
                            </Typography>
                          )}
                          {task.eventEnd && (
                            <Typography
                              variant="body2"
                              color={task.color ? "inherit" : "textSecondary"}
                              style={{ marginTop: 8 }}
                            >
                              {t("tasks.fields.eventEnd")}:{" "}
                              {new Date(task.eventEnd).toLocaleString()}
                            </Typography>
                          )}
                          {!!(task.Assignees || []).length && (
                            <div className={classes.chips}>
                              {task.Assignees.map((user) => (
                                <Chip
                                  key={user.id}
                                  size="small"
                                  variant={
                                    task.backgroundColor || task.color
                                      ? "outlined"
                                      : "default"
                                  }
                                  style={{
                                    color: task.color || undefined,
                                    borderColor: task.color || undefined,
                                  }}
                                  label={userName(user)}
                                />
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </CardActionArea>
                      {!archived && sortMode === "manual" && (
                        <CardActions
                          style={{ justifyContent: "flex-end", paddingTop: 0 }}
                        >
                          <IconButton
                            size="small"
                            color={task.color ? "inherit" : "default"}
                            aria-label={`${t("tasks.moveUp")}: ${task.title}`}
                            disabled={busy || index === 0}
                            onClick={() =>
                              repositionTask(
                                task.id,
                                list.id,
                                cards[index - 1].id,
                              )
                            }
                          >
                            <ArrowUpward fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color={task.color ? "inherit" : "default"}
                            aria-label={`${t("tasks.moveDown")}: ${task.title}`}
                            disabled={busy || index === cards.length - 1}
                            onClick={() =>
                              repositionTask(
                                task.id,
                                list.id,
                                cards[index + 2] ? cards[index + 2].id : null,
                              )
                            }
                          >
                            <ArrowDownward fontSize="small" />
                          </IconButton>
                        </CardActions>
                      )}
                    </Card>
                  ))}
                  {!cards.length && (
                    <Typography
                      color="textSecondary"
                      variant="body2"
                      className={classes.empty}
                    >
                      {t("tasks.emptyList")}
                    </Typography>
                  )}
                  {!archived && (
                    <Button
                      fullWidth
                      startIcon={<Add />}
                      disabled={busy}
                      onClick={() => setSelection({ TaskListId: list.id })}
                    >
                      {t("tasks.addCard")}
                    </Button>
                  )}
                </Paper>
              );
            })}
          </div>
        </>
      )}
      {!loading && <BoardScrollbar boardRef={boardRef} content={board} />}
      {showMembers && (
        <BoardMembersDialog
          board={selectedBoard}
          onClose={() => setShowMembers(false)}
          onChanged={load}
        />
      )}
      {selection && (
        <TaskDialog
          selection={selection}
          lists={board.lists}
          users={board.users}
          onClose={() => setSelection(null)}
          onChanged={load}
        />
      )}
      <Dialog
        open={!!listEditor}
        onClose={() => {
          if (!busy) setListEditor(null);
        }}
        fullWidth
        maxWidth="xs"
        aria-labelledby="task-list-title"
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            saveList();
          }}
        >
          <DialogTitle id="task-list-title">
            {t(
              listEditor && listEditor.id
                ? "tasks.renameList"
                : "tasks.addList",
            )}{" "}
            {listEditor && listEditor.id ? (
              <IconButton
                onClick={() => {
                  deleteList();
                }}
              >
                <DeleteForever></DeleteForever>
              </IconButton>
            ) : null}
          </DialogTitle>
          <DialogContent>
            {listError && <Alert severity="error">{listError}</Alert>}
            <TextField
              autoFocus
              required
              fullWidth
              id="task-list-name"
              label={t("tasks.listName")}
              value={listEditor ? listEditor.name : ""}
              disabled={busy}
              inputProps={{ maxLength: 255 }}
              onChange={(event) =>
                setListEditor({ ...listEditor, name: event.target.value })
              }
            />
          </DialogContent>
          <DialogActions>
            <Button disabled={busy} onClick={() => setListEditor(null)}>
              {t("tasks.close")}
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={busy || !listEditor || !listEditor.name.trim()}
            >
              {t("tasks.save")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
}
